import { useEffect, useRef } from "react";
import {
    Chat,
    ChatStatus,
    ListAgentChatDocument, ListAgentChatQuery, PortalChatEvent,
    PortalEventType, PortalMessageEvent, PortalParticipantEvent,
    PortalReactionEvent, PortalTaskUpdatedEvent, PortalTicketChanged,
} from "../../__generated__/api";
import client from "../../api";
import useNotifications from "../NotificationContext";
import useEvents, { ConnectionEvent } from "../PortalSubscriptionContext";
import useLogging, { LoggingHandler } from "../logging/LoggingContext";
import { TicketStateContext } from "./TicketState";

export default class SocketUpdater {
    public static useSocketUpdater(
        chatsQuery: ListAgentChatQuery | undefined,
        ticketStateContextInstance: TicketStateContext,
    ) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ticketStateContext = useRef(ticketStateContextInstance);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            ticketStateContext.current = ticketStateContextInstance;
        }, [ticketStateContextInstance]);

        const ticketState = ticketStateContext.current.tickets;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const logging = useLogging();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const notifications = useNotifications();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const chatsQueryRef = useRef<ListAgentChatQuery | undefined>(chatsQuery);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEvents(api => {
            api.on(ConnectionEvent.RECONNECTED, 100, () => {
                logging.debug("Reconnected to server");
                ticketState.getTickets().forEach(ticket => ticket.reconnected());
                ticketStateContext.current.refreshLoadedTickets();
                ticketStateContext.current.selectionState.update();
            });
            api.on(PortalEventType.Resync, 0, () => {
                //
            });
            [
                PortalEventType.MessageNew,
                PortalEventType.MessageStatus,
                PortalEventType.NoteAdded,
                PortalEventType.MessageEdited,
                PortalEventType.MessageDeleted,
                PortalEventType.MessageRemoved,
                PortalEventType.MessageRedacted,
                PortalEventType.SystemMessage,
            ].forEach(type => {
                api.on(type, 0, (msg: PortalMessageEvent) => {
                    LoggingHandler.instance.wrap(async () => {
                        const ticket = await ticketState.getOrFetch(msg.chatId);
                        if (!ticket) {
                            return;
                        }
                        const { shouldNotify } = ticket.processMessageEvent(type, msg);
                        if (shouldNotify) {
                            notifications.message(
                                msg.messageId,
                                `New Message: ${ticket.ticket.subject}`,
                                msg.message.plaintext as string,
                            );
                        }
                    });
                });
            });

            api.on(PortalEventType.ConversationDeleted, 0, (msg: PortalReactionEvent) => {
                const ticket = ticketState.get(msg.chatId);
                ticket?.processDeleted();
                ticketState.delete(msg.chatId);
            });

            api.on(PortalEventType.ReactionAdded, 0, (msg: PortalReactionEvent) => {
                const ticket = ticketState.get(msg.chatId);
                ticket?.processReactionEvent(PortalEventType.ReactionAdded, msg);
            });
            api.on(PortalEventType.ReactionRemoved, 0, (msg: PortalReactionEvent) => {
                const ticket = ticketState.get(msg.chatId);
                ticket?.processReactionEvent(PortalEventType.ReactionRemoved, msg);
            });
            api.on(PortalEventType.ConversationNew, 0, async (msg: PortalChatEvent) => {
                if (!msg.chat) {
                    return;
                }
                logging.wrap(async () => {
                    const item = await ticketState.getOrFetch(msg.chatId);
                    if (msg.chat) {
                        item?.resync(msg.chat);
                    }
                    const column = ticketStateContext.current.dashboardView?.addTicket(item);
                    const isColumnVisible = ticketStateContext.current.currentDashboard
                        .ticketStates
                        .some(state => state.ticketStateId === column?.ticketState.id);

                    item.markUnreadLocal();
                    if (!isColumnVisible) {
                        return;
                    }
                    item.highlight();

                    notifications.conversation(
                        msg.chatId,
                        `New ticket: ${item.ticket.subject}`,
                        msg.chat?.lastMessage ?? "New Request",
                    );

                    if (chatsQueryRef.current) {
                        client.writeQuery<ListAgentChatQuery>({
                            query: ListAgentChatDocument,
                            overwrite: true,
                            data: {
                                chats: {
                                    chats: [
                                        ...chatsQueryRef.current.chats?.chats ?? [],
                                        {
                                            ...item.chat as Chat,
                                        },
                                    ],
                                },
                            },
                        });
                    }
                });
            });

            api.on(PortalEventType.ParticipantsAdded, 0, async (evt: PortalParticipantEvent) => {
                logging.wrap(async () => {
                    const ticket = await ticketState.getOrFetch(evt.chatId);
                    ticket?.processParticipants(PortalEventType.ParticipantsAdded, evt);
                });
            });
            api.on(PortalEventType.ParticipantsRemoved, 0, async (evt: PortalParticipantEvent) => {
                logging.wrap(async () => {
                    const ticket = await ticketState.getOrFetch(evt.chatId);
                    ticket?.processParticipants(PortalEventType.ParticipantsRemoved, evt);
                });
            });

            api.on(PortalEventType.ConversationAssigned, 0, async (evt: PortalChatEvent) => {
                logging.wrap(async () => {
                    const ticket = await ticketState.getOrFetch(evt.chatId);
                    ticket?.assignLocal({
                        assigneeId: evt.chat?.assigneeId,
                    });
                    if (evt.chat?.status) {
                        ticket?.setStatusLocal(evt.chat?.status);
                    }
                    if (evt.chat?.isAssignedToCurrentUser && !ticket.isOpened) {
                        ticket.highlight();
                    }
                });
            });

            [
                PortalEventType.ConversationIgnored,
                PortalEventType.HoldStarted,
                PortalEventType.HoldStopped,
                PortalEventType.SnoozeStarted,
                PortalEventType.SnoozeStopped,
                PortalEventType.StatusChanged,
            ].forEach(type => {
                api.on(type, 0, async (evt: PortalChatEvent) => {
                    logging.wrap(async () => {
                        const ticket = await ticketState.getOrFetch(evt.chatId);
                        if (!evt.chat?.status || !ticket) {
                            return;
                        }
                        const isChanged = ticket.status !== evt.chat.status;
                        ticket.setTicketStateIdLocal(evt.chat.stateId, evt.chat.status, evt.chat.assigneeId);
                        if (evt.chat.status !== ChatStatus.Ignored) {
                            ticket.assignLocal({
                                assigneeId: evt.chat.assigneeId,
                                shouldUnassign: evt.chat.assigneeId == null,
                                status: evt.chat.status,
                            });
                        }

                        ticketStateContext.current.selectionState.update();
                        if (isChanged) {
                            setTimeout(() => {
                                ticket.highlight();
                            }, 100);
                        }
                    });
                });
            });

            api.on(PortalEventType.PriorityChanged, 0, (evt: PortalChatEvent) => {
                const ticket = ticketState.get(evt.chatId);
                if (evt.chat?.priority) {
                    ticket?.setPriorityLocal(evt.chat?.priority);
                }
            });
            api.on(PortalEventType.ParticipantMarkRead, 0, (evt: PortalParticipantEvent) => {
                const ticket = ticketState.get(evt.chatId);
                ticket?.markReadLocal();
            });

            api.on(PortalEventType.ParticipantMarkUnread, 0, (evt: PortalParticipantEvent) => {
                const ticket = ticketState.get(evt.chatId);
                ticket?.markUnreadLocal();
            });
            api.on(PortalEventType.TicketStateChanged, 0, async (evt: PortalChatEvent) => {
                const ticket = await ticketState.getOrFetch(evt.chatId);
                ticket?.setTicketStateIdLocal(evt.chat?.stateId, evt.chat?.status, evt.chat?.assigneeId);
                ticketStateContext.current.selectionState.update();
            });

            api.on(PortalEventType.Resync, 0, async (evt: PortalChatEvent) => {
                const ticket = await ticketState.getOrFetch(evt.chatId);
                if (evt.chat) {
                    ticket?.resync(evt.chat);
                } else {
                    ticket?.resyncTicket();
                }
            });

            api.on(PortalEventType.TicketFieldsChanged, 0, (evt: PortalTicketChanged) => {
                const ticket = ticketState.get(evt.chatId);
                if (evt.changedFields) {
                    ticket?.processTicketUpdate(evt.changedFields);
                }
            });

            [
                PortalEventType.TaskCreated,
                PortalEventType.TaskDeleted,
                PortalEventType.TaskUpdated,
            ].forEach(type => {
                api.on(type, 0, async (evt: PortalTaskUpdatedEvent) => {
                    const ticket = ticketState.get(evt.chatId);
                    ticket?.tasks?.processTicketUpdate(evt);
                });
            });
        }, [ticketState]);
    }
}
